/**
 * パスキー（#113、docs/43 の2回目、Figma「画面の案」）の画面の部品。
 *   ・passkeyLogin：ログイン前。options → 端末（顔・指紋・画面ロック）→ verify。メール欄の自動入力（Conditional UI）もボタンも同じ道
 *   ・passkeyRegister：ログイン中。options（海外の IP は api.ts の確認コードの窓）→ 端末 → verify → 設定のログイン方法
 *   ・PasskeyPrompt：ログイン直後に1回だけ「次回からパスキーでログイン」。この製品のパスキーが無い・端末が対応・まだ断っていない ときだけ
 *   ・端末の候補を今の状態に合わせる合図（WebAuthn の Signal API、#114、docs/43 §14）：signalUnknownPasskey・syncPasskeySignals
 *   端末の窓は @simplewebauthn/browser。対応しないブラウザではボタンを出さない（設定は「対応していません」）
 */
import { PASSKEY_ERROR, PASSKEY_MSG, type ChooseResponse, type LoginMethods, type Me, type PasskeyList, type PasskeySignal } from "@genbaryoku/contract";
import {
  WebAuthnAbortService,
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
  type AuthenticationResponseJSON,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { ApiError, api, messageOf } from "../api.js";
import { ErrorText, useToast } from "./ui.jsx";

export type PasskeyLoginResult = (Me & { next?: string | null }) | ChooseResponse;

/** 鍵の印（Figma のパスキーの印） */
export function PasskeyIcon({ size = 20, className = "pkicon" }: { size?: number; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} aria-hidden="true">
      <circle cx="7.5" cy="12" r="4" />
      <path d="M11.5 12H21M17.5 12v3.5M20.5 12v2.5" />
    </svg>
  );
}

/** 端末の窓を閉じた・取りやめた（文は出さない） */
export const isPasskeyCancel = (e: unknown): boolean => e instanceof Error && (e.name === "NotAllowedError" || e.name === "AbortError");

/** 端末の窓のエラー → 文（API のエラーはその文のまま） */
export function passkeyErrorText(e: unknown): string {
  if (e instanceof Error && e.name === "InvalidStateError") return PASSKEY_ERROR.AUTH_PASSKEY_EXISTS; // この端末の鍵はもう登録されている（excludeCredentials）
  // 端末に発見可能な鍵を扱えるものが無い（認証器の無いブラウザ・Linux の Chrome など）
  if (e instanceof Error && e.name === "NotSupportedError") return PASSKEY_MSG.unsupported;
  if (e instanceof Error && e.name === "SecurityError") return PASSKEY_ERROR.AUTH_PASSKEY_VERIFY_FAILED;
  return messageOf(e);
}

/** このブラウザでパスキーが使えるか（ボタン）・メール欄の自動入力が使えるか（Conditional UI） */
export function usePasskeySupport(): { ready: boolean; supported: boolean; autofill: boolean } {
  const [s, setS] = useState({ ready: false, supported: false, autofill: false });
  useEffect(() => {
    let alive = true;
    const supported = browserSupportsWebAuthn();
    void (supported ? browserSupportsWebAuthnAutofill() : Promise.resolve(false))
      .catch(() => false)
      .then((autofill) => alive && setS({ ready: true, supported, autofill }));
    return () => {
      alive = false;
    };
  }, []);
  return s;
}

/** ログイン前の1歩目：options → 端末（顔・指紋・画面ロック）。autofill はメール欄の候補から選ぶ（Conditional UI。選ばれるまで待つ） */
export async function passkeyAssert(autofill: boolean): Promise<AuthenticationResponseJSON> {
  const optionsJSON = await api<PublicKeyCredentialRequestOptionsJSON>("POST", "/auth/passkey/login/options");
  return startAuthentication({ optionsJSON, useBrowserAutofill: autofill });
}

/**
 * ログイン前の2歩目：API が確かめる → ログイン（会社が2つ以上なら会社を選ぶ）。
 * ログインできたら、次に開く画面で端末の候補を合わせる（①、#114）。どこにも無い鍵（AUTH_PASSKEY_UNKNOWN）なら端末に「この鍵は無い」と合図する
 */
export async function passkeyLoginVerify(response: AuthenticationResponseJSON, remember: boolean): Promise<PasskeyLoginResult> {
  try {
    const r = await api<PasskeyLoginResult>("POST", "/auth/passkey/login/verify", { response, remember });
    markAfterPasskeyLogin();
    return r;
  } catch (e) {
    if (e instanceof ApiError && e.code === "AUTH_PASSKEY_UNKNOWN" && typeof e.problem.rpId === "string" && typeof e.problem.credentialId === "string") {
      signalUnknownPasskey(e.problem.rpId, e.problem.credentialId);
    }
    throw e;
  }
}

/** 「パスキーでログイン」のボタン：1歩目と2歩目 */
export async function passkeyLogin(remember: boolean): Promise<PasskeyLoginResult> {
  return passkeyLoginVerify(await passkeyAssert(false), remember);
}

/** ログイン中：パスキーを足す（戻りは設定のログイン方法） */
export async function passkeyRegister(): Promise<LoginMethods> {
  const optionsJSON = await api<PublicKeyCredentialCreationOptionsJSON>("POST", "/auth/passkey/register/options");
  const response = await startRegistration({ optionsJSON });
  return api<LoginMethods>("POST", "/auth/passkey/register/verify", { response });
}

/** 進行中の端末の窓を止める（自動入力を待っているあいだにボタンを押した・画面を離れた） */
export const cancelPasskey = () => WebAuthnAbortService.cancelCeremony();

// ---------------------------------------------------------------------------
// 端末の候補を今の状態に合わせる合図（WebAuthn の Signal API、#114、docs/43 §14）
//   どれも「そのブラウザに関数があるときだけ」呼び、無ければ何もしない。失敗しても画面には何も出さない（console.warn だけ）。
//   合図は「できるだけ伝える」もので、端末が受け取る保証は無い（受け取らなければ候補に残り、押すと「登録されていません」のまま）
// ---------------------------------------------------------------------------
type SignalApi = {
  signalUnknownCredential?: (o: { rpId: string; credentialId: string }) => Promise<void>;
  signalAllAcceptedCredentials?: (o: { rpId: string; userId: string; allAcceptedCredentialIds: string[] }) => Promise<void>;
  signalCurrentUserDetails?: (o: { rpId: string; userId: string; name: string; displayName: string }) => Promise<void>;
};
const signalApi = (): SignalApi | null => (typeof window !== "undefined" && window.PublicKeyCredential ? (window.PublicKeyCredential as unknown as SignalApi) : null);
function fire(name: string, call: () => Promise<void>): void {
  try {
    void Promise.resolve(call()).catch((e: unknown) => console.warn(`[passkey] ${name}`, e));
  } catch (e) {
    console.warn(`[passkey] ${name}`, e);
  }
}

/** この鍵はサーバーに無い（ログインで AUTH_PASSKEY_UNKNOWN）→ 端末がその鍵を候補から消す */
export function signalUnknownPasskey(rpId: string, credentialId: string): void {
  const p = signalApi();
  if (typeof p?.signalUnknownCredential !== "function") return;
  fire("signalUnknownCredential", () => p.signalUnknownCredential!({ rpId, credentialId }));
}

/** この人のこの製品の鍵の一覧（一覧に無い鍵を端末が消す）と、候補に出す名前（今のメール・氏名） */
export function signalPasskeyState(s: PasskeySignal): void {
  const p = signalApi();
  if (!p) return;
  if (typeof p.signalAllAcceptedCredentials === "function") {
    fire("signalAllAcceptedCredentials", () => p.signalAllAcceptedCredentials!({ rpId: s.rpId, userId: s.userId, allAcceptedCredentialIds: s.acceptedCredentialIds }));
  }
  if (typeof p.signalCurrentUserDetails === "function") {
    fire("signalCurrentUserDetails", () => p.signalCurrentUserDetails!({ rpId: s.rpId, userId: s.userId, name: s.name, displayName: s.displayName }));
  }
}

/**
 * ログイン中：今の一覧と名前を API から読んで端末に合図する（②設定「ログイン方法」を開いたとき・③パスキーを外した直後・氏名を変えたとき）。
 * ブラウザに関数が無ければ API も呼ばない
 */
export async function syncPasskeySignals(): Promise<void> {
  const p = signalApi();
  if (typeof p?.signalAllAcceptedCredentials !== "function" && typeof p?.signalCurrentUserDetails !== "function") return;
  try {
    const list = await api<PasskeyList>("GET", "/account/passkeys");
    signalPasskeyState(list.signal);
  } catch (e) {
    console.warn("[passkey] 端末の候補を合わせられなかった", e);
  }
}

// ---------------------------------------------------------------------------
// ログイン直後の案内（1回だけ）と、パスキーでログインした直後の合図（①）
// ---------------------------------------------------------------------------
const JUST_LOGGED_IN = "gb.passkey.justLoggedIn";
const AFTER_PASSKEY_LOGIN = "gb.passkey.afterLogin";

/** パスキーでログインできた（会社を選ぶ画面を通っても）。次に開く画面（Shell）で端末の候補を合わせる */
function markAfterPasskeyLogin(): void {
  try {
    sessionStorage.setItem(AFTER_PASSKEY_LOGIN, "1");
  } catch {
    /* 使えないブラウザでは合図を送らない */
  }
}
function takeAfterPasskeyLogin(): boolean {
  try {
    const v = sessionStorage.getItem(AFTER_PASSKEY_LOGIN) === "1";
    sessionStorage.removeItem(AFTER_PASSKEY_LOGIN);
    return v;
  } catch {
    return false;
  }
}

/** ログインできた直後に呼ぶ（パスワード・Google・Apple・会社を選ぶ）。次に開く画面で1回だけ案内を確かめる */
export function markJustLoggedIn(): void {
  try {
    sessionStorage.setItem(JUST_LOGGED_IN, "1");
  } catch {
    /* 使えないブラウザでは案内を出さない */
  }
}
function takeJustLoggedIn(): boolean {
  try {
    const v = sessionStorage.getItem(JUST_LOGGED_IN) === "1";
    sessionStorage.removeItem(JUST_LOGGED_IN);
    return v;
  } catch {
    return false;
  }
}

/**
 * Figma「案内：次回からパスキーでログイン（ログイン直後に1回だけ）」。Shell が置く。
 * パスキーでログインした直後なら、ここで端末の候補を合わせる合図も送る（①、#114）
 */
export function PasskeyPrompt() {
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const addBtn = useRef<HTMLButtonElement>(null);
  /** 「ログイン直後」の印は1回だけ読む。読んだ結果は ref に置く（開発の StrictMode で effect が2回走っても、2回目で確かめられるように） */
  const pending = useRef<boolean | null>(null);

  useEffect(() => {
    if (takeAfterPasskeyLogin()) void syncPasskeySignals();
    if (pending.current === null) pending.current = takeJustLoggedIn();
    if (!pending.current) return;
    let alive = true;
    void (async () => {
      try {
        if (!browserSupportsWebAuthn() || !(await platformAuthenticatorIsAvailable())) return;
        const list = await api<PasskeyList>("GET", "/account/passkeys");
        if (!alive) return;
        pending.current = false;
        if (list.prompt) setOpen(true);
      } catch {
        /* 案内は出さないだけ */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  useEffect(() => {
    if (!open) return;
    addBtn.current?.focus();
    const k = (e: KeyboardEvent) => e.key === "Escape" && !busy && setOpen(false);
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [open, busy]);

  const later = async () => {
    setBusy(true);
    try {
      await api("POST", "/account/passkey-prompt/dismiss");
    } catch {
      /* 断れなくても閉じる（次のログインでまた出るだけ） */
    }
    setBusy(false);
    setOpen(false);
  };
  const add = async () => {
    setErr(undefined);
    setBusy(true);
    try {
      const m = await passkeyRegister();
      qc.setQueryData(["login-methods"], m);
      setOpen(false);
      toast(PASSKEY_MSG.added);
    } catch (e) {
      if (!isPasskeyCancel(e)) setErr(passkeyErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {open ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && setOpen(false)}>
          <div className="dlg pkprompt" role="dialog" aria-modal="true" aria-labelledby="pk-prompt-h" aria-describedby="pk-prompt-b" data-testid="passkey-prompt">
            <div className="db">
              <span className="pkbadge">
                <PasskeyIcon size={28} />
              </span>
              <div className="dh" id="pk-prompt-h">
                {PASSKEY_MSG.promptTitle}
              </div>
              <p id="pk-prompt-b">{PASSKEY_MSG.promptBody}</p>
              <ErrorText>{err}</ErrorText>
            </div>
            <div className="df">
              <button type="button" className="btn text" disabled={busy} onClick={() => void later()} data-testid="passkey-prompt-later">
                {PASSKEY_MSG.promptLater}
              </button>
              <button type="button" ref={addBtn} className="btn primary" disabled={busy} onClick={() => void add()} data-testid="passkey-prompt-add">
                {PASSKEY_MSG.promptAdd}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {node}
    </>
  );
}
